import tl from "./i18n.js";
import toast from "./toast.js";
import { exposeFunctions } from "./core.js";

/**
 * Make a HTTP request, optionally send JSON data.
 * @param {string} path
 * @param {"POST"|"PUT"|"PATCH"} method
 * @param {Object} data Data to send as JSON
 * @returns {Promise<Response>} Promise of HTTP response
 */
export function sendData(path, method, data) {
	const init = { method };
	if (data) {
		init.body = JSON.stringify(data);
		init.headers = { 'Content-Type': 'application/json' };
	}
	return _fetch(path, init);
}

/**
 * Make a HTTP request to send form-data.
 * @param {string} path
 * @param {"POST"|"PUT"|"PATCH"} method
 * @param {FormData} data
 * @returns {Promise<Response>} Promise of HTTP response
 */
export function sendForm(path, method, data) {
	const init = { method };
	if (data) {
		init.body = data;
		// Content-Type header is set automatically
	}
	return _fetch(path, init);
}

/**
 * Make a HTTP request to send form-urlencoded.
 * @param {string} path
 * @param {"POST"|"PUT"|"PATCH"} method
 * @param {URLSearchParams} data
 * @returns {Promise<Response>}
 */
export function sendParams(path, method, data) {
	const init = { method };
	if (data) {
		init.body = data.toString();
		init.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
	}
	return _fetch(path, init);
}

/**
 * Make a HTTP request
 * @param {string} path
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export function _fetch(path, init) {
	return fetch(path, init)
		.then(response => {
			if (response.ok)
				return response; // return as is, no need to parse
			else
				return getProblemDetail(response);
		})
		.catch(error => {
			console.error(error);
			const problem = { status: 0 };
			problem.detail = tl("Please check your internet connection");
			return asFetchResponse(problem);
		});
}

function genericErrorResponse(status) {
	const problem = { status };
	problem.detail = tl("An error has occurred") + ": " + status;
	return asFetchResponse(problem);
}

/**
 * Extract the error message from the HTTP response.
 * @param {Response} response The HTTP response
 * @returns {Promise<Response>} The error response
 */
export function getProblemDetail(response) {
	const status = response.status;

	const contentType = response.headers.get("Content-Type");
	if (!contentType || !contentType.includes("json"))
		return Promise.resolve(genericErrorResponse(status));

	return response.json()
		.then(problem => {
			if (!problem.status)
				problem.status = status;
			if (!problem.detail)
				problem.detail = problem.message;
			if (!problem.detail)
				return genericErrorResponse(status);
			return asFetchResponse(problem);
		})
		.catch(error => {
			console.error(error);
			return genericErrorResponse(status);
		});
}

/**
 * @param {Object} problem
 * @returns {Response}
 */
function asFetchResponse(problem) {
	return {
		ok: false,
		status: problem.status,
		json: () => Promise.resolve(problem)
	};
}

/**
 * @param {Response} response The HTTP response
 * @returns {Promise}
 */
export function showProblemDetail(response) {
	return response.json().then(problem => {
		toast(problem.detail);
	});
}

/**
 * @param {string} name
 * @returns {string}
 */
export function getCookie(name) {
	const cookies = document.cookie;

	let start = cookies.indexOf(name + "=");
	if (start < 0) // if not found
		return null;
	start += name.length + 1;

	let stop = cookies.indexOf(";", start);
	if (stop < 0) // if not found
		stop = cookies.length;

	const encoded = cookies.substring(start, stop);
	const value = decodeURIComponent(encoded);
	return value;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {string} [path]
 * @param {number} [maxAge]
 */
export function setCookie(name, value, path, maxAge) {
	let v = name + '=' + value;

	if (path)
		v += "; Path=" + path;

	if (maxAge)
		v += "; Max-Age=" + maxAge;

	document.cookie = v;
}

export function isAuthenticated() {
	const expiry = +(getCookie("AccessExpiry") || 0);
	return (expiry * 1000) > Date.now();
}

export default _fetch;

exposeFunctions({ _fetch, sendData, sendForm, sendParams, getCookie, setCookie });

